import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { openNotification } from "../Components/Notification/Index";
import { NoInternetError, RequestTimeoutError } from "../Utils/custom-errors";
import { dispatchGlobalError } from "../Utils/event-dispatcher";

// Create an Axios instance
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL ?? "https://api.qualifyme.ai", // Read the URL from .env and fallback to a default value
  timeout: 60000 * 2, // Adjust the timeout value if needed
});

// Request interceptor to include Bearer token in headers
apiClient.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    let token = localStorage.getItem("access-token"); // Adjust the token retrieval method as needed
    if (token) {
      //token = token.replace(/^"(.*)"$/, "$1");
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle status codes
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Handle successful responses
    return response;
  },
  (error: AxiosError): Promise<AxiosError> => {
    // Handle error responses
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Handle unauthorized error, e.g., redirect to login page
          console.error("Unauthorized, please log in again.");
          if (window.location.pathname !== "/sign-in") {
            window.location.href = "/sign-in";
            console.log("apiClient -> location", window.location.href);
          }
          // You can add a redirect to login here
          break;
        case 403:
          // Application limit reached
          if ((error.response.data as { message: string }).message === "Application limit reached") {
            openNotification("error", "Try after some time", "Company not accepting new applications at this time.");
            break;
          }
          // Handle forbidden error
          console.error(
            "Forbidden, you do not have permission to access this resource."
          );
          if (window.location.pathname !== "/sign-in") {
            window.location.href = "/sign-in";
            console.log("apiClient -> location", window.location.href);
          }
          break;
        case 404:
          // Handle not found error
          console.error("Resource not found.");
          break;
        case 500:
          // Handle internal server error
          console.error("Internal server error, please try again later.");
          break;
        default:
          console.error(`Unexpected error: ${error.response.status}`);
      }
    } else {
      // Timeout case
      if (error.code === 'ECONNABORTED') {
        console.warn("Request timed out.");
        const timeoutError = new Error("REQUEST_TIMEOUT");
        dispatchGlobalError(timeoutError);
        return Promise.reject(new RequestTimeoutError());
      }

      // No internet or general network issue
      if (error.code === 'ERR_NETWORK') {
        console.warn("No internet connection.");
        const noInternetError = new Error("NO_INTERNET");
        dispatchGlobalError(noInternetError);
        return Promise.reject(new NoInternetError());
      }
    }
    return Promise.reject(error);
  }
);



export default apiClient;
